import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { profileService } from '../services/profileService';
import { UserProfile } from '../types';
import Navigation from '../components/layout/Navigation';

const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [profile, setProfile] = useState<Partial<UserProfile>>({
    full_name: '',
    grad_year: new Date().getFullYear() + 1,
    gpa: 0,
    sat_score: 0,
    height_feet: 5,
    height_inches: 0,
    weight: 0,
    erg_2k: '',
    erg_6k: '',
    hometown: '',
    high_school: '',
    club_team: '',
    bio: '',
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Fetch existing profile from Supabase
    const fetchProfile = async () => {
      const existingProfile = await profileService.getProfile(user.id);
      if (existingProfile) {
        setProfile({
          full_name: existingProfile.full_name || '',
          grad_year: existingProfile.grad_year || new Date().getFullYear() + 1,
          gpa: existingProfile.gpa || 0,
          sat_score: existingProfile.sat_score || 0,
          height_feet: existingProfile.height_feet || 5,
          height_inches: existingProfile.height_inches || 0,
          weight: existingProfile.weight || 0,
          erg_2k: existingProfile.erg_2k || '',
          erg_6k: existingProfile.erg_6k || '',
          hometown: existingProfile.hometown || '',
          high_school: existingProfile.high_school || '',
          club_team: existingProfile.club_team || '',
          bio: existingProfile.bio || '',
        });
      }
      setLoading(false);
    };

    fetchProfile();
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setSaving(true);
    setMessage(null);

    try {
      const success = await profileService.updateProfile(user.id, profile);
      if (success) {
        setMessage({ type: 'success', text: 'Profile saved successfully!' });
      } else {
        setMessage({ type: 'error', text: 'Failed to save profile. Please try again.' });
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      setMessage({ type: 'error', text: 'An error occurred while saving your profile.' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setProfile((prev) => ({
      ...prev,
      [name]:
        name.includes('gpa') ||
        name.includes('score') ||
        name.includes('height') ||
        name.includes('weight')
          ? parseFloat(value) || 0
          : value,
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rowing-blue"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-rowing-gray-50">
      <Navigation isAuthenticated={true} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {message && (
          <div
            className={`mb-4 p-4 rounded-lg ${
              message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}
          >
            {message.text}
          </div>
        )}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-rowing-navy px-6 py-4">
            <h1 className="text-2xl font-bold text-white">Athlete Profile</h1>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Personal Information */}
            <div>
              <h2 className="text-lg font-semibold text-rowing-navy mb-4">Personal Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    name="full_name"
                    value={profile.full_name || ''}
                    onChange={handleChange}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Graduation Year
                  </label>
                  <select
                    name="grad_year"
                    value={profile.grad_year || 0}
                    onChange={handleChange}
                    className="input-field"
                    required
                  >
                    {[...Array(5)].map((_, i) => {
                      const year = new Date().getFullYear() + i;
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hometown</label>
                  <input
                    type="text"
                    name="hometown"
                    value={profile.hometown || ''}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="City, State"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    High School
                  </label>
                  <input
                    type="text"
                    name="high_school"
                    value={profile.high_school || ''}
                    onChange={handleChange}
                    className="input-field"
                  />
                </div>
              </div>
            </div>

            {/* Academic Information */}
            <div>
              <h2 className="text-lg font-semibold text-rowing-navy mb-4">Academic Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    GPA (4.0 scale)
                  </label>
                  <input
                    type="number"
                    name="gpa"
                    value={profile.gpa || 0}
                    onChange={handleChange}
                    className="input-field"
                    min="0"
                    max="4.0"
                    step="0.01"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SAT Score</label>
                  <input
                    type="number"
                    name="sat_score"
                    value={profile.sat_score || 0}
                    onChange={handleChange}
                    className="input-field"
                    min="400"
                    max="1600"
                    placeholder="Total score"
                  />
                </div>
              </div>
            </div>

            {/* Athletic Information */}
            <div>
              <h2 className="text-lg font-semibold text-rowing-navy mb-4">Athletic Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Height (ft)
                    </label>
                    <input
                      type="number"
                      name="height_feet"
                      value={profile.height_feet || 0}
                      onChange={handleChange}
                      className="input-field"
                      min="4"
                      max="7"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Height (in)
                    </label>
                    <input
                      type="number"
                      name="height_inches"
                      value={profile.height_inches || 0}
                      onChange={handleChange}
                      className="input-field"
                      min="0"
                      max="11"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Weight (lbs)
                  </label>
                  <input
                    type="number"
                    name="weight"
                    value={profile.weight || 0}
                    onChange={handleChange}
                    className="input-field"
                    min="90"
                    max="300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    2K Erg Time
                  </label>
                  <input
                    type="text"
                    name="erg_2k"
                    value={profile.erg_2k || ''}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="MM:SS.S"
                    pattern="[0-9]{1,2}:[0-9]{2}\.[0-9]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    6K Erg Time
                  </label>
                  <input
                    type="text"
                    name="erg_6k"
                    value={profile.erg_6k || ''}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="MM:SS.S"
                    pattern="[0-9]{1,2}:[0-9]{2}\.[0-9]"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Club Team</label>
                  <input
                    type="text"
                    name="club_team"
                    value={profile.club_team || ''}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="Your rowing club or team"
                  />
                </div>
              </div>
            </div>

            {/* Bio */}
            <div>
              <h2 className="text-lg font-semibold text-rowing-navy mb-4">About Me</h2>
              <textarea
                name="bio"
                value={profile.bio || ''}
                onChange={handleChange}
                className="input-field"
                rows={4}
                placeholder="Tell coaches about yourself, your goals, and what makes you unique..."
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button type="submit" disabled={saving} className="btn-primary">
                {saving ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
